import { useMemo } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { DetailTableLayout } from '@/components/common/DetailTableLayout';
import { LabeledPillList } from '@/components/common/LabeledPill';
import { useInfiniteTableQuery } from '@/hooks/useInfiniteTableQuery';
import { useRememberViewedRow, useRevealLastViewedRow } from '@/hooks/useLastViewedRow';
import { monitorDetailInfiniteQueryOptions } from '../api/question.api';
import { getInstituteSubjectsLink } from '../components/monitorLevelLinks';
import { getMonitorInstituteColumns } from '../components/QuestionColumns';
import { useMonitorContextFields } from '../hooks/useMonitorContextFields';
import type { MonitorInstitutesSearch } from '../types/question.types';
import {
  getInstituteListParentRow,
  getInstituteListTitle,
  toCountPillField,
  toInstituteListParams,
} from '../utils/monitorLevels';

interface MonitorInstitutesPageProps {
  search: MonitorInstitutesSearch;
}

export function MonitorInstitutesPage({ search }: MonitorInstitutesPageProps) {
  const navigate = useNavigate();
  const params = useMemo(() => toInstituteListParams(search), [search]);

  const parentRow = getInstituteListParentRow(search);
  useRememberViewedRow(parentRow.list, parentRow.rowId, parentRow.parentId);
  const lastViewedInstituteId = useRevealLastViewedRow('questionMonitorInstitutes');

  const query = useInfiniteTableQuery(monitorDetailInfiniteQueryOptions(params));
  const contextFields = useMonitorContextFields(params);

  const columns = useMemo(
    () =>
      getMonitorInstituteColumns(search.variant, (row, subjectType, isNew) =>
        getInstituteSubjectsLink(row.projectInstituteId, {
          subjectType,
          isNew,
          projectId: row.projectId ?? undefined,
          userId: row.userId ?? undefined,
          instituteId: row.instituteId ?? undefined,
          from: search.from,
        }),
      ),
    [search.variant, search.from],
  );

  return (
    <DetailTableLayout
      title={getInstituteListTitle(search.isNew)}
      onNoHistory={() =>
        void navigate({ to: search.from, search: { projectId: search.projectId } })
      }
      summary={
        <LabeledPillList
          fields={[
            ...contextFields,
            toCountPillField({
              key: 'count',
              label: 'Institutes',
              count: query.firstPageMeta?.total,
              isLoading: query.isLoading,
            }),
          ]}
        />
      }
      table={{
        rows: query.rows,
        columns,
        isLoading: query.isLoading,
        error: query.loadError,
        errorTitle: 'Could not load the institutes',
        onRetry: () => void query.refetch(),
        stickyColumnId: 'instituteName',
        emptyMessage: 'No institute sits under this row',
        isRowHighlighted: (row) => row.projectInstituteId === lastViewedInstituteId,
        loadMoreProps: query.loadMoreProps,
      }}
    />
  );
}
